import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { NavLink } from "@/components/NavLink";
import { 
  DollarSign, 
  Check, 
  Phone,
  Calendar,
  ArrowRight,
  Shield,
  RefreshCw,
  Zap,
  Clock
} from "lucide-react";

const CANONICAL = "https://www.leafrushmarketing.com/landscaping-website-design-pricing/";

const faqItems = [
  {
    question: "What does $200–$300/month include for a landscaping website?",
    answer: "Your monthly subscription covers: custom website design, hosting, SSL, unlimited content edits, mobile-responsive design, local SEO structure, service pages for hardscaping/plantings/design, service area pages, quote forms, project galleries, and ongoing support."
  },
  {
    question: "Is there a setup fee or upfront build cost?",
    answer: "No. We build your site first at $0 upfront. You only start paying the monthly subscription after you approve the design and your site goes live."
  },
  {
    question: "Why subscription pricing for landscapers?",
    answer: "Landscaping projects are visual and trust-driven. Your website needs ongoing updates (new project photos, seasonal services). Subscription pricing keeps your site fresh and maintained without hourly charges."
  },
  {
    question: "What determines where I fall in the $200–$300 range?",
    answer: "Your monthly rate depends on business size, number of services (patios, plantings, hardscape, outdoor living, etc.), service areas, and overall website scope. We confirm your exact rate after a quick fit call."
  },
  {
    question: "Can I cancel my landscaping website subscription anytime?",
    answer: "Yes. Month-to-month, no contracts. We earn your business by keeping your website valuable. If you cancel, we export your content so you can move elsewhere."
  },
  {
    question: "How do I add new project photos or services?",
    answer: "Just send them to us. Reasonable updates are included in your subscription—new project photos, service additions, service area expansions. No extra charge for standard requests."
  },
  {
    question: "Do landscapers need more pages than lawn care companies?",
    answer: "Often yes. Landscaping businesses typically have more service categories (design, hardscape, plantings, outdoor living, lighting) that each deserve dedicated pages. This can affect where you fall in the pricing range."
  },
  {
    question: "What if I only do hardscaping?",
    answer: "We build for your specialty. Whether you focus on patios and retaining walls or full-service landscaping, we structure your site to showcase what you actually do—not generic landscaping content."
  },
  {
    question: "Is there a discount for paying annually?",
    answer: "We occasionally offer seasonal discounts and can discuss annual options during your fit call. Reach out to learn about current offers."
  },
  {
    question: "What's NOT included in the subscription?",
    answer: "Domain registration ($15–$20/year, you own it directly), paid advertising spend, and premium third-party integrations (like advanced CRM systems) are separate. Everything else is covered."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL}#faq`,
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  "url": CANONICAL,
  "name": "Landscaping Website Design Pricing | Leaf Rush Marketing",
  "description": "Landscaping website design pricing: $200–$300/mo, $0 upfront. See exactly what's included and why subscription beats one-time builds for landscapers.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg"
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${CANONICAL}#service`,
  "name": "Landscaping Website Design Subscription",
  "description": "Professional landscaping website design with subscription pricing. $200–$300/month, $0 upfront. Includes hosting, maintenance, SEO setup, project galleries, and ongoing support.",
  "provider": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "serviceType": "Website Design Subscription"
};

const LandscapingWebsiteDesignPricing = () => {
  return (
    <>
      <SEO 
        title="Landscaping Website Design Pricing: $200–$300/Mo"
        description="Landscaping website design pricing: $200–$300/mo, $0 upfront. See exactly what's included and why subscription beats one-time builds for landscapers."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="landscaping website design pricing, landscaping website subscription price, monthly landscaping website cost, landscaper website design pricing"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge className="mb-4">Pricing Breakdown</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Landscaping Website Design Pricing Explained
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              No hidden fees, no surprise invoices. Here's exactly what you pay and what you get with our landscaping website subscription.
            </p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <DollarSign className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">Quick Answer: What Does It Cost?</h2>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>$200–$300/month</strong> — full website subscription (7–14+ pages)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>$0 upfront</strong> — no build fee, no setup cost</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Month-to-month</strong> — cancel anytime, no contracts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>All-inclusive</strong> — hosting, SSL, maintenance, gallery updates, support</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#get-started">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Phone className="mr-2 h-4 w-4" />
                      Get Started
                    </Button>
                  </a>
                  <NavLink to="/websites/">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      See Full Pricing Details
                    </Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What's Included in Your Monthly Subscription</h2>
            <p className="text-muted-foreground mb-8">
              Unlike one-time builds where you pay extra for hosting, maintenance, and updates, our subscription bundles everything:
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Zap, title: "Custom Website Design", desc: "7–14+ pages built specifically for landscaping businesses" },
                { icon: Shield, title: "Hosting & SSL Security", desc: "Fast, secure hosting with HTTPS included" },
                { icon: RefreshCw, title: "Unlimited Content Edits", desc: "Add projects, update services, expand areas—no extra charge" },
                { icon: Clock, title: "Ongoing Maintenance", desc: "We keep your site updated, fast, and secure" },
                { icon: Check, title: "Project Galleries", desc: "Before/after photos that sell your work" },
                { icon: Check, title: "Service Pages", desc: "Dedicated pages for patios, plantings, hardscape, etc." },
                { icon: Check, title: "Local SEO Structure", desc: "Service area pages that rank in your target cities" },
                { icon: Check, title: "Quote Forms & Click-to-Call", desc: "Convert visitors into leads instantly" },
              ].map((item, index) => (
                <Card key={index}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <item.icon className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <h3 className="font-semibold">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Learn more about our <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> process and features.
            </p>
          </div>
        </section>

        {/* Subscription vs One-Time */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Subscription vs. One-Time Build: The Real Math</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-muted">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">One-Time Build (Typical)</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between">
                      <span>Upfront build cost</span>
                      <span className="font-semibold">$3,000–$7,000</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Annual hosting</span>
                      <span className="font-semibold">$100–$300</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Annual maintenance</span>
                      <span className="font-semibold">$300–$600</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Gallery updates (5/yr)</span>
                      <span className="font-semibold">$250–$500</span>
                    </li>
                    <li className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Year 1 Total</span>
                      <span className="font-bold">$3,650–$8,400</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-primary">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="font-bold text-lg">Subscription (Leaf Rush)</h3>
                    <Badge variant="secondary" className="text-xs">Recommended</Badge>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between">
                      <span>Upfront cost</span>
                      <span className="font-semibold text-primary">$0</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Monthly subscription</span>
                      <span className="font-semibold">$200–$300</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Hosting included</span>
                      <span className="font-semibold text-primary">$0</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Maintenance included</span>
                      <span className="font-semibold text-primary">$0</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Gallery updates included</span>
                      <span className="font-semibold text-primary">$0</span>
                    </li>
                    <li className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Year 1 Total</span>
                      <span className="font-bold text-primary">$2,400–$3,600</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground mt-6 text-center">
              Plus, you're never locked in. Cancel anytime if it's not working for you.
            </p>
          </div>
        </section>

        {/* What Affects Price */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What Affects Your Monthly Rate</h2>
            <p className="text-muted-foreground mb-8">
              Everyone gets the same high-quality website. The range ($200–$300) reflects differences in scope:
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Number of Service Categories</h3>
                  <p className="text-sm text-muted-foreground">
                    Patios, plantings, hardscape, outdoor living, lighting—each may need dedicated <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">service pages</NavLink>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Service Area Size</h3>
                  <p className="text-sm text-muted-foreground">
                    Each city needs a dedicated <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area page</NavLink> to rank locally
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Project Portfolio Depth</h3>
                  <p className="text-sm text-muted-foreground">
                    Extensive before/after galleries with project details require more setup
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              After a 15-minute fit call, we'll confirm your exact rate. No guessing, no surprises.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to See Your Exact Pricing?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Book a 15-minute call. We'll discuss your landscaping business, confirm your monthly rate, and answer any questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#get-started">
                <Button size="lg">
                  <Calendar className="mr-2 h-4 w-4" />
                  Get Started
                </Button>
              </a>
              <NavLink to="/contact/">
                <Button size="lg" variant="outline">
                  Contact Us
                </Button>
              </NavLink>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <NavLink to="/landscaping-website-design/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                <p className="text-sm text-muted-foreground">Full details on what we build and how it converts</p>
              </NavLink>
              <NavLink to="/websites/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Website Subscription Pricing</h3>
                <p className="text-sm text-muted-foreground">Our complete pricing page with FAQs</p>
              </NavLink>
              <NavLink to="/landscaping-portfolio-website/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Landscaping Portfolio Website</h3>
                <p className="text-sm text-muted-foreground">How project galleries drive high-ticket leads</p>
              </NavLink>
              <NavLink to="/growth-add-ons/gbp/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Google Business Profile Optimization</h3>
                <p className="text-sm text-muted-foreground">Boost local visibility alongside your website</p>
              </NavLink>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Landscaping Website Pricing FAQ</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call" className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your 15-Minute Fit Call</h2>
              <p className="text-muted-foreground">
                We'll discuss your landscaping business, confirm your exact monthly rate, and answer any questions.
              </p>
            </div>
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteDesignPricing;
